import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DateTime } from 'luxon';
import { forkJoin, of, startWith } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { DeepPartial } from '../../../../ts-extensions';
import {
  CaseImage,
  FindingsControls,
  FindingsFormGroup,
  IncidentFormControls,
  IncidentFormValues,
  NtfReasonOption,
  TransactionItemTiny,
} from '../../../../types';
import { incidentFormValidator, linkValidator, normalizeIncidentFormValues, validateComments } from './form-utils';

const OTHER_NTF_REASON_CATEGORY = 'Other';

@Injectable({ providedIn: 'root' })
export class ReviewFormService {
  private fb = inject(FormBuilder);
  private lpService = inject(LossPreventionService);

  public uniqueInstancePhotoUrls = signal<string[]>([]);
  public uniqueTransactionPhotoUrls = signal<string[]>([]);
  public initialInstancePhotoUrls = signal<string[]>([]);
  public initialTransactionPhotoUrls = signal<string[]>([]);
  public caseImages = signal<CaseImage[]>([]);
  public isLoadingCaseImages = signal<boolean>(false);
  public hasKioskPhoto = signal<boolean>(false);

  public findingsForm: FindingsFormGroup = this.fb.group<FindingsControls>({
    describeClothing: this.fb.control<string>(''),
    describeSnackDrink: this.fb.control<string>(''),
    howManyItemsScanned: this.fb.control<string>(''),
    kioskInteractionResult: this.fb.control<string>(''),
    kioskScannedItems: this.fb.control<string | null>(null),
    kioskUnscannedItems: this.fb.control<string | null>(null),
    costOfFalseItem: this.fb.control<string>(''),
    paymentMethod: this.fb.control<string>(''),
    estimatedTotal: this.fb.control<string>(''),
    numPaidItems: this.fb.control<number>(0),
    numUnpaidItems: this.fb.control<number>(0),
    numSnacksDrinksSelected: this.fb.control<number>(0),
    numPaymentAttempts: this.fb.control<number>(0),
    numItemsScanned: this.fb.control<number>(0),
    totalActualItemsObserved: this.fb.control<number>(0),
    numItemsOverlooked: this.fb.control<number>(0),
    numItemsSelectedAfterTransaction: this.fb.control<number>(0),
    numSnacksDrinksRemoved: this.fb.control<number>(0),
    numTotalItems: this.fb.control<number>(0),
    comments: this.fb.control<string>('', { validators: validateComments }),
    ntfReason: this.fb.control<NtfReasonOption | null>(null),
  });

  // note: avoid field-level validators because we're using errors object at the form level
  public form: FormGroup<IncidentFormControls> = this.fb.group(
    {
      isTheft: this.fb.control(false),
      reviewOutcomeId: this.fb.control(null),
      suspectedItems: this.fb.array<FormControl<TransactionItemTiny>>([]),
      matchingCase: this.fb.control('new_case'),
      additionalStolenValue: this.fb.control(0),
      instance_at: this.fb.control(null),
      theftType: this.fb.group({
        is_cancellation: this.fb.control(false),
        is_underringing: this.fb.control(false),
        is_walkout: this.fb.control(false),
        is_cashier: this.fb.control(false),
      }),
      uniqueInstancePhoto: this.fb.array<FormControl<File>>([]),
      existingUniqueInstancePhoto: this.fb.array<FormControl<string>>([]),
      reports: this.fb.array<FormControl<File>>([]),
      existingReports: this.fb.array<FormControl<string>>([]),
      // we can use field-level validator since we're handling it in the component-level
      extraLinks: this.fb.array<FormControl<string>>([this.fb.control('', linkValidator)]),
      didNotReturn: this.fb.control(false),
      // I think this is only for walkout incident form, I'll double-check with Ian (I think he's the one who worked on it)
      selectedImages: this.fb.array<FormControl<string>>([]),
      useKioskPhoto: this.fb.control(false),
    },
    { validators: incidentFormValidator },
  );

  public useKioskPhoto = toSignal<boolean>(this.form.controls.useKioskPhoto.valueChanges);

  public ntfReason = toSignal(
    this.findingsForm.controls.ntfReason.valueChanges.pipe(startWith(this.findingsForm.controls.ntfReason.value)),
  );
  public ntfComments = toSignal(
    this.findingsForm.controls.comments.valueChanges.pipe(startWith(this.findingsForm.controls.comments.value)),
  );

  public isFormSubmittable() {
    if (this.form.controls.isTheft.value) return this.form.valid && this.findingsForm.valid;
    const ntfReason = this.findingsForm.controls.ntfReason.value;
    if (!ntfReason) return false;
    if (ntfReason.category === OTHER_NTF_REASON_CATEGORY) return this.findingsForm.controls.comments.valid;
    return true;
  }

  public getNtfReason(): string {
    const ntfReason = this.findingsForm.controls.ntfReason.value;
    if (!ntfReason) return '';
    if (ntfReason.category === OTHER_NTF_REASON_CATEGORY) return this.findingsForm.controls.comments.value?.trim() ?? '';
    return ntfReason.label;
  }

  public resetForm(): void {
    this.form.reset();
    this.form.controls.uniqueInstancePhoto.clear();
    this.form.controls.reports.clear();
    this.form.controls.existingReports.clear();
    this.form.controls.existingUniqueInstancePhoto.clear();
    this.form.controls.selectedImages.clear();
    this.form.controls.suspectedItems.clear();
    this.form.controls.extraLinks.clear();
    this.form.controls.extraLinks.push(this.fb.control('', linkValidator));
    this.form.controls.matchingCase.setValue('new_case');
    this.form.controls.isTheft.setValue(false);
    this.uniqueInstancePhotoUrls.set([]);
    this.uniqueTransactionPhotoUrls.set([]);
    this.form.updateValueAndValidity();
    this.form.markAsPristine();
    this.caseImages.set([]);
    this.form.controls.useKioskPhoto.setValue(false);
    this.resetFindingsForm();
  }

  public applyInitialValues(initialValues: DeepPartial<IncidentFormValues>): void {
    this.resetFindingsForm();
    const formValues = normalizeIncidentFormValues(initialValues);
    const {
      suspectedItems,
      extraLinks,
      existingReports,
      existingUniqueInstancePhoto,
      selectedImages,
      uniqueInstancePhoto,
      reports,
      ...otherValues
    } = formValues;

    this.form.patchValue(otherValues as Partial<IncidentFormValues>);

    if (formValues.comments !== undefined) {
      this.findingsForm.controls.comments.setValue(formValues.comments ?? '');
    }

    // Replace extra links form array
    const extraLinksArray = this.form.controls.extraLinks;
    extraLinksArray.clear();
    (extraLinks || []).forEach((link) => extraLinksArray.push(this.fb.control(link, linkValidator)));

    // Suspected items
    const suspectedItemsArray = this.form.controls.suspectedItems;
    suspectedItemsArray.clear();
    (suspectedItems || []).forEach((item) => suspectedItemsArray.push(this.fb.control(item)));

    // Existing reports/photos (string links)
    const existingReportsArray = this.form.controls.existingReports;
    existingReportsArray.clear();
    (existingReports || []).forEach((photo) => existingReportsArray.push(this.fb.control(photo)));
    this.initialTransactionPhotoUrls.set(existingReports || []);
    const existingInstancePhotosArray = this.form.controls.existingUniqueInstancePhoto;
    existingInstancePhotosArray.clear();
    (existingUniqueInstancePhoto || []).forEach((photo) => existingInstancePhotosArray.push(this.fb.control(photo)));
    this.initialInstancePhotoUrls.set(existingUniqueInstancePhoto || []);
    // Selected images (recent walk-ins)
    const selectedImagesArray = this.form.controls.selectedImages;
    selectedImagesArray.clear();
    (selectedImages || []).forEach((img) => selectedImagesArray.push(this.fb.control(img)));

    // New files for photos/reports
    if (uniqueInstancePhoto && uniqueInstancePhoto.length > 0) {
      this.addFilesToFormArray('uniqueInstancePhoto', uniqueInstancePhoto);
    }
    if (reports && reports.length > 0) {
      this.addFilesToFormArray('reports', reports);
    }

    // Emit validation after applying values
    this.findingsForm.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity();
  }

  public loadCasesByLocation(locationId: number | undefined | null): void {
    if (!locationId) {
      this.caseImages.set([]);
      this.isLoadingCaseImages.set(false);
      return;
    }

    this.isLoadingCaseImages.set(true);
    this.caseImages.set([]);
    this.lpService
      .getCasesByLocationSelfService(locationId)
      .pipe(finalize(() => this.isLoadingCaseImages.set(false)))
      .subscribe((cases) => this.caseImages.set(cases));
  }

  public processReportsFileEvent(files: File[]): Promise<void> {
    return this.onReportsFileUpload(files);
  }

  public removeFileAt(control: FormArray<FormControl<File>>, urls: WritableSignal<string[]>, index: number): void {
    control.removeAt(index);
    control.markAsDirty();
    control.updateValueAndValidity();
    urls.update((current) => current.filter((_, i) => i !== index));
  }

  public uploadFiles() {
    const review = this.form.value as IncidentFormValues;
    const hasNewPhotos = review.uniqueInstancePhoto !== null && review.uniqueInstancePhoto.length > 0;
    const hasNewReports = review.reports !== null && review.reports.length > 0;

    return forkJoin([
      hasNewPhotos ? this.lpService.uploadFiles(review.uniqueInstancePhoto as File[]) : of<string[]>([]),
      hasNewReports ? this.lpService.uploadFiles(review.reports as File[]) : of<string[]>([]),
    ]).pipe(
      map(([newPhotoLinks, newReportLinks]) => ({
        uploadedPhotosLinks: [...(newPhotoLinks ?? []), ...review.existingUniqueInstancePhoto],
        uploadedReportsLinks: [...(newReportLinks ?? []), ...review.existingReports],
      })),
    );
  }

  public async onPhotosFileUpload(files: File[]): Promise<string[]> {
    this.form.controls.uniqueInstancePhoto.markAsDirty();
    this.addFilesToFormArray('uniqueInstancePhoto', files);
    this.uniqueInstancePhotoUrls.set([]);
    const urls = await this.filesToDataUrls(files);
    this.uniqueInstancePhotoUrls.set(urls);
    return urls;
  }

  private async onReportsFileUpload(files: File[]): Promise<void> {
    this.form.controls.reports.markAsDirty();
    this.addFilesToFormArray('reports', files);
    this.uniqueTransactionPhotoUrls.set([]);
    const urls = await this.filesToDataUrls(files);
    this.uniqueTransactionPhotoUrls.set(urls);
  }

  private addFilesToFormArray(controlName: keyof IncidentFormControls & string, files: File[]): void {
    if (!files || files.length === 0) return;
    const formArray = this.form.get(controlName) as FormArray<FormControl<File>> | null;
    if (!formArray) return;
    formArray.clear();
    files.forEach((file) => formArray.push(this.fb.control(file)));
  }

  private filesToDataUrls(files: File[]): Promise<string[]> {
    const readers = files.map(
      (file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve((e.target?.result as string) ?? '');
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        }),
    );
    return Promise.all(readers);
  }

  public getValidInstanceAt(reviewInstanceAt: string | Date | null, fallbackDate: Date | null): string | null {
    if (!reviewInstanceAt) {
      if (!fallbackDate) return null;
      const fallbackResult = DateTime.fromJSDate(fallbackDate);
      return fallbackResult.isValid ? fallbackResult.toISO({ includeOffset: false }) : null;
    }

    const dateTime =
      typeof reviewInstanceAt === 'string'
        ? DateTime.fromISO(reviewInstanceAt)
        : reviewInstanceAt instanceof Date
          ? DateTime.fromJSDate(reviewInstanceAt)
          : null;

    return dateTime?.isValid ? dateTime.toISO({ includeOffset: false }) : null;
  }

  private resetFindingsForm(): void {
    this.findingsForm.reset({
      describeClothing: '',
      describeSnackDrink: '',
      estimatedTotal: '',
      howManyItemsScanned: '',
      kioskInteractionResult: '',
      kioskScannedItems: null,
      kioskUnscannedItems: null,
      costOfFalseItem: '',
      paymentMethod: '',
      numPaidItems: 0,
      numUnpaidItems: 0,
      numTotalItems: 0,
      numPaymentAttempts: 0,
      numSnacksDrinksSelected: 0,
      numItemsScanned: 0,
      totalActualItemsObserved: 0,
      numItemsOverlooked: 0,
      numItemsSelectedAfterTransaction: 0,
      numSnacksDrinksRemoved: 0,
      comments: '',
      ntfReason: null,
    });
    this.findingsForm.markAsPristine();
    this.findingsForm.updateValueAndValidity({ emitEvent: false });
  }
}
